// Subscription plan definitions and utilities

// Define all subscription plans
export const SUBSCRIPTION_PLANS = [
  {
    id: 'free',
    name: 'Free',
    description: 'Perfect for trying out our service',
    monthlyPrice: 0,
    annualPrice: 0,
    billingPeriods: ['monthly', 'annual'],
    features: [
      { name: 'Limited chat', limit: '5/day', included: true },
      { name: 'Small documents', limit: '≤5 pages', included: true },
      { name: 'Cases', limit: '3 maximum', included: true },
      { name: 'Watermark PDFs', included: true },
      { name: 'Voice features', included: false },
    ],
    stripePriceIds: {},
  },
  {
    id: 'civilian_plus',
    name: 'Civilian Plus',
    description: 'Great for individuals with regular legal needs',
    monthlyPrice: 9.99,
    annualPrice: 7.99, // Billed at $95.88/year
    billingPeriods: ['monthly', 'annual'],
    features: [
      { name: 'Unlimited chat', description: 'fair-use', included: true },
      { name: 'Document pages', limit: '50-page docs', included: true },
      { name: 'Unlimited cases', included: true },
      { name: 'Text-to-Speech (TTS)', included: true },
      { name: 'Email & PDF generation', included: true },
      { name: 'Template library access', included: true },
    ],
    popular: true,
    stripePriceIds: {
      monthly: 'price_1SPKUBD8lHq4co6YvF4WbyJ2',
      annual: 'price_1STv7SD8lHq4co6Y20AsNfGW',
    },
  },
  {
    id: 'civilian_pro',
    name: 'Civilian Pro',
    description: 'Ideal for active users with complex needs',
    monthlyPrice: 19.99,
    annualPrice: 15.99, // Billed at $191.88/year
    billingPeriods: ['monthly', 'annual'],
    features: [
      { name: 'Document pages', limit: '150-page docs', included: true },
      { name: 'Priority OCR', included: true },
      { name: 'Case Locker (workspace)', included: true },
      { name: 'Share links', included: true },
      { name: 'All Plus features', included: true },
    ],
    stripePriceIds: {
      monthly: 'price_1SPKVZD8lHq4co6YGd237P1n',
      annual: 'price_1STv8OD8lHq4co6Y88Na3LO7',
    },
  },
  {
    id: 'lawyer_pro',
    name: 'Lawyer Pro',
    description: 'Professional tools for legal practitioners',
    monthlyPrice: 49.99,
    annualPrice: 39.99, // Billed at $479.88/year
    billingPeriods: ['monthly', 'annual'],
    features: [
      { name: 'Advanced retrieval', included: true },
      { name: 'Batch analysis', included: true },
      { name: 'Bluebook/DE citation styles', included: true },
      { name: 'Clause heatmaps', included: true },
      { name: 'Export to Word', included: true },
      { name: 'API access', included: true },
      { name: 'All Civilian Pro features', included: true },
    ],
    stripePriceIds: {
      monthly: 'price_1SPKXAD8lHq4co6YUU37Q8AS',
      annual: 'price_1STv91D8lHq4co6YTsOd9dV5',
    },
  },
  {
    id: 'firm',
    name: 'Firm',
    description: 'Enterprise solution for law firms',
    monthlyPrice: null,
    annualPrice: null,
    billingPeriods: ['annual'],
    features: [
      { name: 'Unlimited everything', included: true },
      { name: 'Team collaboration', included: true },
      { name: 'Custom integrations', included: true },
      { name: 'Dedicated support', included: true },
      { name: 'SLA guarantee', included: true },
      { name: 'All Lawyer Pro features', included: true },
    ],
    comingSoon: true,
    stripePriceIds: {
      annual: 'price_1STv9qD8lHq4co6YlIJAuYz3',
    },
  },
];

// Map Stripe price IDs to plan IDs for easy lookup
export const PRICE_ID_TO_PLAN_MAP = {};
export const PLAN_ID_TO_PRICE_IDS_MAP = {};

SUBSCRIPTION_PLANS.forEach(plan => {
  PLAN_ID_TO_PRICE_IDS_MAP[plan.id] = { ...plan.stripePriceIds };
  
  if (plan.stripePriceIds.monthly) {
    PRICE_ID_TO_PLAN_MAP[plan.stripePriceIds.monthly] = plan.id;
  }
  if (plan.stripePriceIds.annual) {
    PRICE_ID_TO_PLAN_MAP[plan.stripePriceIds.annual] = plan.id;
  }
});

// Utility functions
export function getPlanById(id) {
  return SUBSCRIPTION_PLANS.find(plan => plan.id === id);
}

export function getPlanByPriceId(priceId) {
  const planId = PRICE_ID_TO_PLAN_MAP[priceId];
  return planId ? getPlanById(planId) : undefined;
}

export function getPriceIdForPlan(planId, billingPeriod) {
  const plan = getPlanById(planId);
  return plan?.stripePriceIds[billingPeriod];
}

export function getAllActivePlans() {
  return SUBSCRIPTION_PLANS.filter(plan => !plan.comingSoon);
}

export function getAvailableBillingPeriods(planId) {
  const plan = getPlanById(planId);
  return plan?.billingPeriods || ['monthly'];
}

export function isEnterprisePlan(planId) {
  const plan = getPlanById(planId);
  return plan?.enterprise || plan?.comingSoon || false;
}

export function formatPrice(price) {
  if (price === null) return 'Custom';
  return `$${price.toFixed(2)}`;
}

export function calculateAnnualSavings(plan) {
  if (plan.monthlyPrice === null || plan.annualPrice === null) return null;
  const monthlyCost = plan.monthlyPrice * 12;
  const annualCost = plan.annualPrice * 12;
  return monthlyCost - annualCost;
}